import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import PremiumGate from '@/components/premium/PremiumGate.tsx';
import { ROUTES } from '@/config/routes.ts';
import { clubSidebar } from '@/config/sidebarConfig.ts';
import {
  deleteClubCoverApi,
  getClubApi,
  updateClubApi,
  uploadClubCoverApi,
  uploadClubLogoApi,
} from '@/lib/api/clubsApi';
import { getFileUrl } from '@/lib/file-url';
import { queryKeys } from '@/lib/query/queryKeys';
import {
  citycodeFieldOptional,
  emailFieldOptional,
  entityNameField,
  freeTextFieldOptional,
  phoneFieldOptional,
  placeNameField,
  postcodeFieldOptional,
} from '@/lib/validation/fields';
import { useAuthStore } from '@/stores/auth-store';
import { getNameInitials, isClubProActive } from '@shared';
import type { AddressSuggestion, Club } from '@shared/types/entities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, ChevronDown, Eye, ImageIcon, Info, Map, MapPin, Phone, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';

import { AddressAutocomplete } from '../../components/geo/AddressAutocomplete';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { LeafletMap } from '../../components/map/mapLeaflet';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';

// ─── Styles ───────────────────────────────────────────────────
const inputClass = 'w-full px-4 py-3 border border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none';
const labelClass = 'block text-sm font-semibold text-slate-900 mb-2';
const errorClass = 'mt-1 text-xs text-red-600';

// ─── Helpers ──────────────────────────────────────────────────
const AVATAR_BG = ['bg-emerald-500', 'bg-blue-500', 'bg-orange-500', 'bg-purple-500', 'bg-cyan-500', 'bg-amber-500'];

// ─── Zod schema ───────────────────────────────────────────────
const ClubProfileSchema = z.object({
  name: entityNameField('Nom du club', { min: 2, max: 100 }),
  description: freeTextFieldOptional(2000),
  address: freeTextFieldOptional(255),
  city: placeNameField('Ville'),
  department: placeNameField('Département'),
  region: placeNameField('Région'),
  postcode: postcodeFieldOptional,
  citycode: citycodeFieldOptional,
  email: emailFieldOptional,
  phone: phoneFieldOptional,
  registrationNumber: freeTextFieldOptional(50),
});

type ClubForm = z.infer<typeof ClubProfileSchema> & {
  latitude: number | null;
  longitude: number | null;
};

type FormErrors = Partial<Record<keyof ClubForm, string>>;

// ─── UI helper: Accordion section (mobile) ────────────────────
function MobileAccordionSection(props: {
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const { title, icon, defaultOpen = false, children } = props;

  return (
    <details open={defaultOpen} className='bg-white rounded-xl border border-slate-200 overflow-hidden'>
      <summary className='list-none cursor-pointer select-none px-4 py-4'>
        <div className='flex items-center justify-between gap-3'>
          <div className='flex items-center gap-2 min-w-0'>
            {icon}
            <span className='font-bold text-slate-900 truncate'>{title}</span>
          </div>
          <ChevronDown className='w-5 h-5 text-slate-500 shrink-0 transition-transform duration-200 group-open:rotate-180' />
        </div>
      </summary>

      <div className='px-4 pb-4 pt-0'>{children}</div>
    </details>
  );
}

// ─── EditForm ─────────────────────────────────────────────────
function EditForm({ club }: { club: Club }) {
  const user = useAuthStore(s => s.user);
  const isProActive = isClubProActive(user?.club?.clubPlan, user?.club?.subscriptionStatus);
  const queryClient = useQueryClient();

  const VITE_URL_FRONT = (import.meta.env.VITE_URL_FRONT as string | undefined) ?? '';

  const logoFile = club.files?.find(f => f.usage === 'CLUB_LOGO') ?? null;
  const coverFile = club.files?.find(f => f.usage === 'CLUB_COVER') ?? null;

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(logoFile ? getFileUrl(logoFile) : null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [selectedCoverFile, setSelectedCoverFile] = useState<File | null>(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | null>(coverFile ? getFileUrl(coverFile) : null);
  const [coverPendingDelete, setCoverPendingDelete] = useState(false);
  const coverInputRef = useRef<HTMLInputElement | null>(null);

  const [form, setForm] = useState<ClubForm>({
    name: club.name ?? '',
    description: club.description ?? '',
    address: club.address ?? '',
    city: club.city ?? '',
    department: club.department ?? '',
    region: club.region ?? '',
    postcode: club.postcode ?? '',
    citycode: club.citycode ?? '',
    email: club.email ?? '',
    phone: club.phone ?? '',
    registrationNumber: club.registrationNumber ?? '',
    latitude: club.latitude ?? null,
    longitude: club.longitude ?? null,
  });

  const [errors, setErrors] = useState<FormErrors>({});

  const initialForm = useMemo<ClubForm>(
    () => ({
      name: club.name ?? '',
      description: club.description ?? '',
      address: club.address ?? '',
      city: club.city ?? '',
      department: club.department ?? '',
      region: club.region ?? '',
      postcode: club.postcode ?? '',
      citycode: club.citycode ?? '',
      email: club.email ?? '',
      phone: club.phone ?? '',
      registrationNumber: club.registrationNumber ?? '',
      latitude: club.latitude ?? null,
      longitude: club.longitude ?? null,
    }),
    [club],
  );

  const isDirty = useMemo(() => {
    return (
      (Object.keys(initialForm) as (keyof ClubForm)[]).some(k => form[k] !== initialForm[k]) ||
      !!selectedCoverFile ||
      coverPendingDelete ||
      !!selectedFile
    );
  }, [form, initialForm, selectedCoverFile, coverPendingDelete, selectedFile]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  function setField<K extends keyof ClubForm>(key: K, value: ClubForm[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  function validateField(key: keyof z.infer<typeof ClubProfileSchema>, value: string) {
    const shape = ClubProfileSchema.shape[key];
    if (!shape) return;
    const result = shape.safeParse(value);
    setErrors(prev => ({
      ...prev,
      [key]: result.success ? undefined : result.error.issues[0]?.message,
    }));
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  }

  function handleCoverChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedCoverFile(file);
    setCoverPreviewUrl(URL.createObjectURL(file));
    setCoverPendingDelete(false);
  }

  function handleAddressSelect(suggestion: AddressSuggestion) {
    setForm(prev => ({
      ...prev,
      address: suggestion.label,
      city: suggestion.city ?? prev.city,
      postcode: suggestion.postcode ?? prev.postcode,
      citycode: suggestion.citycode ?? prev.citycode,
      latitude: suggestion.latitude,
      longitude: suggestion.longitude,
    }));
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const result = ClubProfileSchema.safeParse(form);
      if (!result.success) {
        const fieldErrors = result.error.flatten().fieldErrors;
        setErrors(Object.fromEntries(Object.entries(fieldErrors).map(([k, v]) => [k, v?.[0]])));
        throw new Error('Formulaire invalide');
      }

      if (selectedFile) {
        await uploadClubLogoApi(club.id, selectedFile);
        setSelectedFile(null);
      }

      if (coverPendingDelete && coverFile) {
        await deleteClubCoverApi(coverFile.id);
        setCoverPendingDelete(false);
      }

      if (selectedCoverFile) {
        await uploadClubCoverApi(club.id, selectedCoverFile);
        setSelectedCoverFile(null);
      }

      const payload = {
        ...(form.name && { name: form.name }),
        ...(form.description !== undefined && { description: form.description }),
        ...(form.address !== undefined && { address: form.address }),
        ...(form.city && { city: form.city }),
        ...(form.department && { department: form.department }),
        ...(form.region && { region: form.region }),
        ...(form.postcode !== undefined && { postcode: form.postcode }),
        ...(form.citycode !== undefined && { citycode: form.citycode }),
        ...(form.latitude != null && { latitude: form.latitude }),
        ...(form.longitude != null && { longitude: form.longitude }),
        ...(form.email !== undefined && { email: form.email }),
        ...(form.phone !== undefined && { phone: form.phone }),
        registrationNumber: form.registrationNumber || null,
      };

      return updateClubApi(club.id, payload);
    },
    onSuccess: () => {
      toast.success('Profil mis à jour');
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.detail(club.id) });
    },
    onError: (err: Error) => {
      if (err.message !== 'Formulaire invalide') toast.error(err.message ?? 'Erreur lors de la sauvegarde');
    },
  });

  const avatarBg = AVATAR_BG[club.id % AVATAR_BG.length];
  const hasCoords = form.latitude != null && form.longitude != null;

  // ---- Reusable section contents (to avoid duplicating logic) ----
  const personalizationContent = (
    <PremiumGate
      isAllowed={isProActive}
      title='Personnalisation de votre page publique (Pro)'
      description='Ajouter un logo et une image de couverture à votre club.'
      ctaLabel='Voir l’abonnement'
      ctaTo='/club/settings?tab=subscription'
    >
      {/* Logo */}
      <div className='bg-white rounded-xl border border-slate-200 p-4 sm:p-6 w-full'>
        <h2 className='text-lg font-bold text-slate-900 mb-6 flex items-center gap-2'>
          <Info className='w-5 h-5 text-blue-600' />
          Logo du club
        </h2>

        <div className='flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6'>
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={club.name}
              className='w-28 h-28 sm:w-32 sm:h-32 rounded-3xl object-cover shadow-lg shrink-0'
            />
          ) : (
            <div
              className={`w-28 h-28 sm:w-32 sm:h-32 ${avatarBg} rounded-3xl flex items-center justify-center text-white font-extrabold text-4xl shadow-lg shrink-0`}
            >
              {getNameInitials(club.name)}
            </div>
          )}

          <div className='min-w-0'>
            <button
              type='button'
              onClick={() => fileInputRef.current?.click()}
              className='w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:border-blue-300 transition text-sm font-semibold text-slate-700'
            >
              <Camera className='w-4 h-4' />
              {logoFile || previewUrl ? 'Changer le logo' : 'Mettre à jour le logo'}
            </button>

            <input
              ref={fileInputRef}
              type='file'
              accept='image/jpeg,image/png,image/gif,image/webp'
              onChange={handleFileChange}
              className='hidden'
            />

            <p className='text-xs text-slate-500 mt-2'>JPG, PNG ou GIF. Max 10 Mo.</p>
            <p className='text-xs text-slate-500'>Recommandé : 300×300 px (carré).</p>
          </div>
        </div>
      </div>

      {/* Cover */}
      <div className='bg-white rounded-xl border border-slate-200 p-4 sm:p-6 w-full'>
        <h2 className='text-lg font-bold text-slate-900 mb-6 flex items-center gap-2'>
          <ImageIcon className='w-5 h-5 text-purple-600' />
          Bannière du club
        </h2>

        {coverPreviewUrl && !coverPendingDelete ? (
          <div className='relative'>
            <img
              src={coverPreviewUrl}
              alt='Bannière'
              className='w-full h-40 sm:h-48 object-cover object-top rounded-xl'
            />

            <div className='absolute top-3 right-3 flex flex-col sm:flex-row gap-2'>
              <button
                type='button'
                onClick={() => coverInputRef.current?.click()}
                className='flex items-center justify-center gap-1.5 px-3 py-1.5 bg-white/90 backdrop-blur rounded-lg text-xs font-semibold text-slate-700 hover:bg-white transition shadow-sm'
              >
                <Camera className='w-3.5 h-3.5' />
                Changer
              </button>

              <button
                type='button'
                onClick={() => {
                  setCoverPendingDelete(true);
                  setSelectedCoverFile(null);
                  setCoverPreviewUrl(null);
                }}
                className='flex items-center justify-center gap-1.5 px-3 py-1.5 bg-red-500/90 backdrop-blur rounded-lg text-xs font-semibold text-white hover:bg-red-600 transition shadow-sm'
              >
                <Trash2 className='w-3.5 h-3.5' />
                Supprimer
              </button>
            </div>
          </div>
        ) : (
          <div className='flex flex-col items-center justify-center h-40 sm:h-48 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50 px-4'>
            <ImageIcon className='w-10 h-10 text-slate-300 mb-2' />
            <button
              type='button'
              onClick={() => coverInputRef.current?.click()}
              className='w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:border-blue-300 transition text-sm font-semibold text-slate-700'
            >
              <Camera className='w-4 h-4' />
              Ajouter une bannière
            </button>
            <p className='text-xs text-slate-500 mt-2 text-center'>Recommandé : 1920×480 px (format paysage).</p>
          </div>
        )}

        <input
          ref={coverInputRef}
          type='file'
          accept='image/jpeg,image/png,image/gif,image/webp'
          onChange={handleCoverChange}
          className='hidden'
        />
      </div>
    </PremiumGate>
  );

  const baseInfoContent = (
    <div className='bg-white rounded-xl border border-slate-200 p-4 sm:p-6 w-full'>
      <h2 className='text-lg font-bold text-slate-900 mb-6 flex items-center gap-2'>
        <Info className='w-5 h-5 text-emerald-600' />
        Informations de base
      </h2>

      <div className='space-y-5'>
        <div>
          <label className={labelClass}>
            Nom du club <span className='text-red-600'>*</span>
          </label>
          <input
            type='text'
            value={form.name}
            onChange={e => setField('name', e.target.value)}
            onBlur={e => validateField('name', e.target.value)}
            className={inputClass}
          />
          {errors.name && <p className={errorClass}>{errors.name}</p>}
        </div>

        {/*<div>*/}
        {/*  <label className={labelClass}>Numéro FFP</label>*/}
        {/*  <input*/}
        {/*    type='text'*/}
        {/*    value={form.registrationNumber}*/}
        {/*    onChange={e => setField('registrationNumber', e.target.value)}*/}
        {/*    onBlur={e => validateField('registrationNumber', e.target.value)}*/}
        {/*    placeholder='Ex : 69-0145-2020'*/}
        {/*    className={inputClass}*/}
        {/*  />*/}
        {/*</div>*/}

        <div>
          <label className={labelClass}>Description</label>
          <textarea
            rows={6}
            value={form.description}
            onChange={e => setField('description', e.target.value)}
            placeholder='Décrivez votre club, votre histoire, vos terrains…'
            className={inputClass}
          />
          {errors.description && <p className={errorClass}>{errors.description}</p>}
        </div>
      </div>
    </div>
  );

  const locationContent = (
    <div className='bg-white rounded-xl border border-slate-200 p-4 sm:p-6 w-full'>
      <h2 className='text-lg font-bold text-slate-900 mb-6 flex items-center gap-2'>
        <MapPin className='w-5 h-5 text-purple-600' />
        Localisation
      </h2>

      <div className='space-y-5'>
        <div>
          <label className={labelClass}>Adresse</label>
          <AddressAutocomplete
            value={form.address ?? ''}
            onChange={value => setField('address', value)}
            onSelect={handleAddressSelect}
            placeholder='Rechercher une adresse…'
          />
        </div>

        <div className='grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5'>
          <div>
            <label className={labelClass}>
              Ville <span className='text-red-600'>*</span>
            </label>
            <input
              type='text'
              value={form.city}
              onChange={e => setField('city', e.target.value)}
              onBlur={e => validateField('city', e.target.value)}
              className={inputClass}
            />
            {errors.city && <p className={errorClass}>{errors.city}</p>}
          </div>

          <div>
            <label className={labelClass}>Code postal</label>
            <input
              type='text'
              value={form.postcode}
              onChange={e => setField('postcode', e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>
              Département <span className='text-red-600'>*</span>
            </label>
            <input
              type='text'
              value={form.department}
              onChange={e => setField('department', e.target.value)}
              onBlur={e => validateField('department', e.target.value)}
              className={inputClass}
            />
            {errors.department && <p className={errorClass}>{errors.department}</p>}
          </div>
        </div>

        <div>
          <label className={labelClass}>
            Région <span className='text-red-600'>*</span>
          </label>
          <input
            type='text'
            value={form.region}
            onChange={e => setField('region', e.target.value)}
            onBlur={e => validateField('region', e.target.value)}
            className={inputClass}
          />
          {errors.region && <p className={errorClass}>{errors.region}</p>}
        </div>

        <div>
          <label className={labelClass}>Aperçu carte</label>
          {hasCoords ? (
            <div className='h-56 sm:h-64 rounded-lg overflow-hidden border border-slate-200'>
              <LeafletMap
                key={`${form.latitude ?? 0}-${form.longitude ?? 0}`}
                lat={form.latitude ?? undefined}
                lng={form.longitude ?? undefined}
              />
            </div>
          ) : (
            <div className='h-56 sm:h-64 bg-slate-100 rounded-lg flex items-center justify-center px-6'>
              <div className='text-center'>
                <Map className='w-12 h-12 text-slate-300 mx-auto mb-2' />
                <p className='text-sm text-slate-400'>
                  Sélectionnez une adresse via l&apos;autocomplétion pour afficher la carte
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const contactContent = (
    <div className='bg-white rounded-xl border border-slate-200 p-4 sm:p-6 w-full'>
      <h2 className='text-lg font-bold text-slate-900 mb-6 flex items-center gap-2'>
        <Phone className='w-5 h-5 text-amber-600' />
        Contact
      </h2>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5'>
        <div>
          <label className={labelClass}>Email de contact</label>
          <input
            type='email'
            value={form.email}
            onChange={e => setField('email', e.target.value)}
            onBlur={e => validateField('email', e.target.value)}
            placeholder='contact@votre-club.fr'
            className={inputClass}
          />
          {errors.email && <p className={errorClass}>{errors.email}</p>}
        </div>

        <div>
          <label className={labelClass}>Téléphone</label>
          <input
            type='tel'
            value={form.phone}
            onChange={e => setField('phone', e.target.value)}
            onBlur={e => validateField('phone', e.target.value)}
            placeholder='Ex : 04 78 12 34 56'
            className={inputClass}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className='p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto'>
      <PageHeader
        title='Profil du club'
        subtitle='Gérez les informations publiques de votre club'
        actions={
          <div className='flex flex-col sm:flex-row gap-3 w-full sm:w-auto'>
            <a
              href={`${VITE_URL_FRONT}/clubs/${club.id}`}
              target='_blank'
              rel='noreferrer'
              className='w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl hover:border-blue-300 transition'
            >
              <Eye className='w-5 h-5 text-slate-600' />
              <span className='text-sm font-semibold text-slate-700'>
                <span className={'hidden md:inline-block'}>Mon</span> Profil public
              </span>
            </a>

            <button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              className='w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition shadow-lg shadow-blue-200 disabled:opacity-50 cursor-pointer'
            >
              <Save className='w-5 h-5' />
              <span className='text-sm font-bold'>{saveMutation.isPending ? 'Enregistrement…' : 'Enregistrer'}</span>
            </button>
          </div>
        }
      />

      {/* MOBILE: accordéons */}
      <div className='md:hidden space-y-4'>
        <MobileAccordionSection
          title='Personnalisation (Pro)'
          icon={<ImageIcon className='w-5 h-5 text-purple-600 shrink-0' />}
          defaultOpen={true}
        >
          <div className='space-y-4'>{personalizationContent}</div>
        </MobileAccordionSection>

        <MobileAccordionSection
          title='Informations de base'
          icon={<Info className='w-5 h-5 text-emerald-600 shrink-0' />}
          defaultOpen={true}
        >
          {baseInfoContent}
        </MobileAccordionSection>

        <MobileAccordionSection title='Localisation' icon={<MapPin className='w-5 h-5 text-purple-600 shrink-0' />}>
          {locationContent}
        </MobileAccordionSection>

        <MobileAccordionSection title='Contact' icon={<Phone className='w-5 h-5 text-amber-600 shrink-0' />}>
          {contactContent}
        </MobileAccordionSection>
      </div>

      {/* DESKTOP: sections normales */}
      <div className='hidden md:block space-y-6 sm:space-y-8'>
        {personalizationContent}
        {baseInfoContent}
        {locationContent}
        {contactContent}
      </div>

      {/* Bottom Actions */}
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-end pt-4 pb-6 sm:pb-8 gap-3'>
        <Link
          to={ROUTES.CLUB_DASHBOARD}
          onClick={e => {
            if (isDirty && !window.confirm('Vous avez des modifications non sauvegardées. Quitter sans sauvegarder ?'))
              e.preventDefault();
          }}
          className='w-full sm:w-auto px-6 py-2 border border-slate-200 rounded-xl hover:border-slate-300 transition text-sm font-semibold text-slate-700 text-center'
        >
          Annuler
        </Link>

        <button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className='w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition shadow-lg shadow-blue-200 disabled:opacity-50 cursor-pointer'
        >
          <Save className='w-5 h-5' />
          <span className='text-sm font-bold'>
            {saveMutation.isPending ? 'Enregistrement…' : 'Enregistrer les modifications'}
          </span>
        </button>
      </div>
    </div>
  );
}

// ─── Page shell ───────────────────────────────────────────────
export default function ClubProfileEdit() {
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const clubId = user?.clubId;

  const {
    data: club,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.clubs.detail(clubId ?? 0),
    queryFn: () => getClubApi(clubId!),
    enabled: !!clubId && isAuthenticated,
  });

  return (
    <DashboardLayout sidebarConfig={clubSidebar}>
      {isLoading && (
        <div className='p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6'>
          <div className='h-10 bg-slate-100 rounded-xl animate-pulse w-1/2 sm:w-1/3' />

          <div className='bg-white rounded-xl border border-slate-200 p-6 animate-pulse'>
            <div className='flex flex-col sm:flex-row gap-6'>
              <div className='w-32 h-32 bg-slate-200 rounded-3xl' />
              <div className='flex-1 space-y-3'>
                <div className='h-4 bg-slate-200 rounded w-1/3' />
                <div className='h-8 bg-slate-200 rounded w-2/3' />
              </div>
            </div>
          </div>
        </div>
      )}

      {isError && (
        <div className='p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto'>
          <QueryErrorState onRetry={refetch} />
        </div>
      )}

      {!isLoading && !isError && !clubId && (
        <div className='p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto'>
          <div className='bg-amber-50 border border-amber-200 rounded-xl p-6 text-amber-700 text-sm font-semibold'>
            Votre compte n&apos;est associé à aucun club.
          </div>
        </div>
      )}

      {club && <EditForm club={club} />}
    </DashboardLayout>
  );
}
